import React, { useState } from "react";
import {
  Card,
  Badge,
  Space,
  Progress,
  Typography,
  Row,
  Col,
  Statistic,
  Button,
  Tooltip,
  Tag,
} from "antd";
import { motion } from "framer-motion";
import moment from "moment-timezone";
import {
  LineChartOutlined,
  MailOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  ThunderboltOutlined,
  UserOutlined,
  PauseCircleOutlined,
  StopOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import PulseReportProgressDetailModal from "./PulseReportProgressDetailModal";

const { Text } = Typography;

// Styled Components
const StyledCard = styled(Card)`
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border-radius: 16px;
  border: none;
  height: 100%;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15);

  .ant-card-body {
    padding: 20px;
  }

  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(24, 144, 255, 0.2);
  }
`;

const InfoCard = styled(Card)`
  background: rgba(255, 255, 255, 0.15);
  border: none;
  border-radius: 12px;
  backdrop-filter: blur(10px);
  margin-top: 16px;

  .ant-card-body {
    padding: 12px;
  }
`;

const ActionButton = styled(Button)`
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
  height: 40px;
  margin-top: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.4);
    color: white;
  }

  &:active {
    background: rgba(255, 255, 255, 0.25);
    color: white;
  }
`;

// Helper Functions
const formatTimeRemaining = (seconds) => {
  if (!seconds && seconds !== 0) return "Calculating...";
  if (seconds === 0) return "Almost done...";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (remainingSeconds > 0 || parts.length === 0)
    parts.push(`${remainingSeconds}s`);

  return parts.join(" ");
};

// Animated Value Component
const ProgressValue = ({ value }) => (
  <motion.span
    key={value}
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.2 }}
  >
    {value}
  </motion.span>
);

const PulseReportProgressCard = ({
  pulseReport,
  progress,
  onPause,
  onStop,
  pauseLoading,
  stopLoading,
}) => {
  const [detailVisible, setDetailVisible] = useState(false);

  if (!pulseReport) return null;

  // Set default progress data if not yet available
  const progressData = progress || {
    progress: {
      sent: 0,
      delivered: 0,
      failed: 0,
      total: 0,
      percentage: 0,
    },
    sendingRate: { current: 0, totalSenders: 0, effectiveRate: 0 },
    recentActivity: { lastFiveMinutes: 0 },
    timing: {},
  };

  // Generate status badge based on percentage
  const getStatusBadge = () => {
    const percentage = Number(progressData.progress.percentage);
    if (percentage === 0) return "processing";
    if (percentage < 50) return "processing";
    if (percentage < 100) return "success";
    return "success";
  };

  return (
    <>
      <StyledCard>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Pulse Report Header */}
          <Space
            align="center"
            style={{
              marginBottom: 16,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Space>
              <MailOutlined style={{ color: "white", fontSize: 18 }} />
              <Text style={{ color: "white", fontSize: 16, fontWeight: 600 }}>
                {pulseReport.name}
              </Text>
            </Space>
            <Badge
              status={getStatusBadge()}
              text={<Text style={{ color: "white" }}>Sending</Text>}
            />
          </Space>

          {/* Progress Section */}
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <Text style={{ color: "white" }}>Progress</Text>
              <Text style={{ color: "white" }}>
                <ProgressValue
                  value={`${progressData.progress.sent} / ${progressData.progress.total} emails`}
                />
              </Text>
            </div>
            <Tooltip title={`${progressData.progress.percentage}% complete`}>
              <Progress
                percent={Number(progressData.progress.percentage)}
                strokeColor="white"
                trailColor="rgba(255,255,255,0.3)"
                showInfo={false}
              />
            </Tooltip>
            <div style={{ textAlign: "center", marginTop: 4 }}>
              <Text style={{ color: "white", fontWeight: 600 }}>
                <ProgressValue value={`${progressData.progress.percentage}%`} />
              </Text>
            </div>
          </div>

          {/* Sending Stats */}
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Space align="center">
                <ThunderboltOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>
                  <ProgressValue
                    value={`${progressData.sendingRate.current || progressData.sendingRate.effectiveRate || 0}/hour`}
                  />
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space align="center">
                <UserOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>
                  <ProgressValue
                    value={`${progressData.sendingRate.totalSenders || 1} senders`}
                  />
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space align="center">
                <RiseOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>
                  <ProgressValue
                    value={`${progressData.recentActivity?.lastFiveMinutes || 0} in 5m`}
                  />
                </Text>
              </Space>
            </Col>
            <Col span={12}>
              <Space align="center">
                <ClockCircleOutlined style={{ color: "white" }} />
                <Text style={{ color: "white" }}>
                  <ProgressValue
                    value={
                      progressData.timing?.estimatedTimeRemaining
                        ? formatTimeRemaining(
                            progressData.timing.estimatedTimeRemaining
                          )
                        : "Calculating..."
                    }
                  />
                </Text>
              </Space>
            </Col>
          </Row>

          {/* Completion Time */}
          {progressData.timing?.estimatedCompletionTime && (
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Tag color="white" style={{ color: "#096dd9", fontWeight: 500 }}>
                Est. completion:{" "}
                {moment(progressData.timing.estimatedCompletionTime).format(
                  "HH:mm"
                )}
              </Tag>
            </div>
          )}

          {/* Delivery Stats */}
          <InfoCard>
            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Statistic
                  title={
                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                      Delivered
                    </Text>
                  }
                  value={progressData.progress.delivered}
                  valueStyle={{ color: "white", fontSize: 18 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title={
                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                      Failed
                    </Text>
                  }
                  value={progressData.progress.failed}
                  valueStyle={{
                    color:
                      progressData.progress.failed > 0 ? "#FFCCC7" : "white",
                    fontSize: 18,
                  }}
                />
              </Col>
            </Row>
          </InfoCard>

          {/* Checkpoint Info */}
          {progressData.checkpoint && (
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Tag color="rgba(255,255,255,0.2)" style={{ color: "white" }}>
                Checkpoint: {progressData.checkpoint.processedCount} processed
              </Tag>
            </div>
          )}

          {/* Action Buttons */}
          <Row gutter={[8, 8]} style={{ marginTop: 16 }}>
            <Col span={12}>
              <ActionButton
                icon={<PauseCircleOutlined />}
                onClick={onPause}
                loading={pauseLoading}
                block
              >
                Pause
              </ActionButton>
            </Col>
            <Col span={12}>
              <ActionButton
                icon={<StopOutlined />}
                onClick={onStop}
                loading={stopLoading}
                danger
                block
                style={{
                  background: "rgba(255, 77, 79, 0.3)",
                  borderColor: "rgba(255, 77, 79, 0.5)",
                }}
              >
                Stop
              </ActionButton>
            </Col>
          </Row>

          {/* View Details Button */}
          <ActionButton
            icon={<LineChartOutlined />}
            onClick={() => setDetailVisible(true)}
            block
          >
            View Detailed Progress
          </ActionButton>
        </motion.div>
      </StyledCard>

      <PulseReportProgressDetailModal
        visible={detailVisible}
        pulseReport={pulseReport}
        progress={progress}
        onClose={() => setDetailVisible(false)}
      />
    </>
  );
};

export default PulseReportProgressCard;
